import React, { useState, useEffect, useRef } from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { getTenant } from '../../utils/tenantUtils';
import '../../css/BankAccountNavigation.css';

const BankAccountNavigation = ({ bankAccounts, currentBankAccountId, activeTab, setActiveTab }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAccountsDropdown, setShowAccountsDropdown] = useState(false);
  const [showViewDropdown, setShowViewDropdown] = useState(false);
  const [visibleAccounts, setVisibleAccounts] = useState([]);
  const [dropdownAccounts, setDropdownAccounts] = useState([]);
  const [showOnlyAccountsButton, setShowOnlyAccountsButton] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef(null);
  const allBillsButtonRef = useRef(null);
  const moreButtonRef = useRef(null);
  const navigate = useNavigate();
  const tenant = getTenant();

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 992);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Calculate how many accounts can fit on one line by measuring actual button widths
  useEffect(() => {
    const calculateLayout = () => {
      if (!bankAccounts || bankAccounts.length === 0 || !containerRef.current) {
        setVisibleAccounts([]);
        setDropdownAccounts([]);
        setShowOnlyAccountsButton(false);
        return;
      }

      // Wait for DOM to be ready
      setTimeout(() => {
        if (!containerRef.current) return;

        const container = containerRef.current;
        const containerWidth = container.offsetWidth;
        const gap = 8; // Gap between buttons
        
        // Measure "All Bills" button width (use ref if available, otherwise estimate)
        let allBillsWidth = 80; // Default estimate
        if (allBillsButtonRef.current) {
          allBillsWidth = allBillsButtonRef.current.offsetWidth;
        } else {
          // If ref not available yet, estimate based on screen size
          const isMobile = window.innerWidth < 576;
          allBillsWidth = isMobile ? 70 : 80;
        }
        let usedWidth = allBillsWidth + gap;
        
        // Estimate "+X more" button width (we'll measure it if needed)
        const estimateMoreButtonWidth = (count) => {
          // "+X more" + icon + padding ≈ 70-100px depending on count
          return 70 + (count.toString().length * 8);
        };

        // Try to fit accounts one by one
        const accountsToShow = [];
        const accountsToHide = [];
        
        // First, try to fit accounts + "+X more" button
        for (let i = 0; i < bankAccounts.length; i++) {
          // Create a temporary button to measure its width
          const tempBtn = document.createElement('button');
          tempBtn.className = 'bank-account-btn';
          tempBtn.style.position = 'absolute';
          tempBtn.style.visibility = 'hidden';
          tempBtn.textContent = bankAccounts[i].nickname;
          document.body.appendChild(tempBtn);
          const accountBtnWidth = tempBtn.offsetWidth;
          document.body.removeChild(tempBtn);
          
          // Check if we can fit this account + "+X more" button
          const remainingAccounts = bankAccounts.length - i - 1;
          const moreBtnWidth = remainingAccounts > 0 ? estimateMoreButtonWidth(remainingAccounts) : 0;
          const totalNeeded = usedWidth + accountBtnWidth + gap + (remainingAccounts > 0 ? moreBtnWidth + gap : 0);
          
          if (totalNeeded <= containerWidth) {
            accountsToShow.push(bankAccounts[i]);
            usedWidth += accountBtnWidth + gap;
          } else {
            // Can't fit this account, put rest in dropdown
            accountsToHide.push(...bankAccounts.slice(i));
            break;
          }
        }
        
        // If we can't fit any accounts + "+X more", try just "All Bills" + "+X more"
        if (accountsToShow.length === 0 && bankAccounts.length > 0) {
          const moreBtnWidth = estimateMoreButtonWidth(bankAccounts.length);
          const totalNeeded = allBillsWidth + gap + moreBtnWidth;
          
          if (totalNeeded <= containerWidth) {
            // Can fit "All Bills" + "+X more", show all accounts in dropdown
            setVisibleAccounts([]);
            setDropdownAccounts(bankAccounts);
            setShowOnlyAccountsButton(false);
          } else {
            // Can't even fit "All Bills" + "+X more", show only "ACCOUNTS" button
            setVisibleAccounts([]);
            setDropdownAccounts(bankAccounts);
            setShowOnlyAccountsButton(true);
          }
        } else {
          setVisibleAccounts(accountsToShow);
          setDropdownAccounts(accountsToHide);
          setShowOnlyAccountsButton(false);
        }
      }, 100);
    };

    calculateLayout();
    
    // Recalculate on window resize
    const handleResize = () => calculateLayout();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', calculateLayout);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', calculateLayout);
    };
  }, [bankAccounts]);

  const handleAccountClick = (accountId) => {
    const path = tenant ? `/$/${tenant}/track/${accountId}` : `/track/${accountId}`;
    navigate(path);
  };

  const handleAllBillsClick = () => {
    const path = tenant ? `/$/${tenant}/track` : '/track';
    navigate(path);
  };

  if (!bankAccounts || bankAccounts.length === 0) {
    return null;
  }

  const handleAccountsButtonClick = () => {
    setShowAccountsDropdown(!showAccountsDropdown);
  };

  const handleViewOptionClick = (tab) => {
    if (setActiveTab) {
      setActiveTab(tab);
    }
    setShowViewDropdown(false);
  };

  // Mobile layout: two dropdowns
  if (isMobile) {
    return (
      <div className="bank-account-navigation mobile-bank-nav" ref={containerRef} style={{ overflow: 'visible' }}>
        <div className="bank-accounts-buttons-container mobile-two-dropdowns" style={{ overflow: 'visible' }}>
          <Dropdown 
            show={showAccountsDropdown} 
            onToggle={(isOpen, e) => {
              if (e) {
                e.preventDefault();
                e.stopPropagation();
              }
              setShowAccountsDropdown(isOpen);
            }}
            align="start"
          >
            <Dropdown.Toggle
              variant={!currentBankAccountId ? "primary" : "outline-primary"}
              className="bank-account-btn mobile-dropdown-btn"
              id="mobile-accounts-dropdown"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowAccountsDropdown(!showAccountsDropdown);
              }}
            >
              Bank Accounts
              <FontAwesomeIcon icon={faChevronDown} className="ms-1" size="sm" />
            </Dropdown.Toggle>
            <Dropdown.Menu show={showAccountsDropdown}>
              <Dropdown.Item
                onClick={() => {
                  handleAllBillsClick();
                  setShowAccountsDropdown(false);
                }}
                active={!currentBankAccountId}
              >
                All Bills
              </Dropdown.Item>
              {bankAccounts.map((account) => (
                <Dropdown.Item
                  key={account.id}
                  onClick={() => {
                    handleAccountClick(account.id);
                    setShowAccountsDropdown(false);
                  }}
                  active={currentBankAccountId === account.id}
                  title={`${account.nickname} - ${account.bank}`}
                >
                  {account.nickname}
                  <small className="text-muted d-block">{account.bank}</small>
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          {setActiveTab && (
            <Dropdown 
              show={showViewDropdown} 
              onToggle={(isOpen, e) => {
                if (e) {
                  e.preventDefault();
                  e.stopPropagation();
                }
                setShowViewDropdown(isOpen);
              }}
              align="start"
            >
              <Dropdown.Toggle
                variant="outline-primary"
                className="bank-account-btn mobile-dropdown-btn"
                id="mobile-view-dropdown"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowViewDropdown(!showViewDropdown);
                }}
              >
                View
                <FontAwesomeIcon icon={faChevronDown} className="ms-1" size="sm" />
              </Dropdown.Toggle>
              <Dropdown.Menu show={showViewDropdown}>
                <Dropdown.Item
                  onClick={() => handleViewOptionClick("#fortnight")}
                  active={activeTab === "#fortnight"}
                >
                  This Fortnight
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => handleViewOptionClick("#future")}
                  active={activeTab === "#future"}
                >
                  Future Bills
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => handleViewOptionClick("#recent")}
                  active={activeTab === "#recent"}
                >
                  Recently Paid
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => handleViewOptionClick("#search")}
                  active={activeTab === "#search"}
                >
                  Search Bills
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>
      </div>
    );
  }

  // Desktop layout: original with buttons
  return (
    <div className="bank-account-navigation" ref={containerRef}>
      <div className="bank-accounts-buttons-container">
        {showOnlyAccountsButton ? (
          // Show only "ACCOUNTS" button that opens dropdown with all accounts
          <Dropdown show={showAccountsDropdown} onToggle={setShowAccountsDropdown}>
            <Dropdown.Toggle
              variant={!currentBankAccountId ? "primary" : "outline-primary"}
              className="bank-account-btn"
              id="all-accounts-dropdown"
            >
              Accounts
              <FontAwesomeIcon icon={faChevronDown} className="ms-1" size="sm" />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={handleAllBillsClick}
                active={!currentBankAccountId}
              >
                All Bills
              </Dropdown.Item>
              {bankAccounts.map((account) => (
                <Dropdown.Item
                  key={account.id}
                  onClick={() => handleAccountClick(account.id)}
                  active={currentBankAccountId === account.id}
                  title={`${account.nickname} - ${account.bank}`}
                >
                  {account.nickname}
                  <small className="text-muted d-block">{account.bank}</small>
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        ) : (
          <>
            {/* All Bills button - always first */}
            <Button
              ref={allBillsButtonRef}
              variant={!currentBankAccountId ? "primary" : "outline-primary"}
              className="bank-account-btn all-bills-btn"
              onClick={handleAllBillsClick}
            >
              All Bills
            </Button>

            {/* Visible bank account buttons */}
            {visibleAccounts.map((account) => (
              <Button
                key={account.id}
                variant={currentBankAccountId === account.id ? "primary" : "outline-primary"}
                className="bank-account-btn"
                onClick={() => handleAccountClick(account.id)}
                title={`${account.nickname} - ${account.bank}`}
              >
                {account.nickname}
              </Button>
            ))}

            {/* Dropdown for remaining accounts */}
            {dropdownAccounts.length > 0 && (
              <Dropdown show={showDropdown} onToggle={setShowDropdown}>
                <Dropdown.Toggle
                  ref={moreButtonRef}
                  variant="outline-primary"
                  className="bank-account-btn dropdown-btn"
                  id="more-accounts-dropdown"
                >
                  +{dropdownAccounts.length} more
                  <FontAwesomeIcon icon={faChevronDown} className="ms-1" size="sm" />
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  {dropdownAccounts.map((account) => (
                    <Dropdown.Item
                      key={account.id}
                      onClick={() => handleAccountClick(account.id)}
                      active={currentBankAccountId === account.id}
                      title={`${account.nickname} - ${account.bank}`}
                    >
                      {account.nickname}
                      <small className="text-muted d-block">{account.bank}</small>
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BankAccountNavigation;
